import { lazy, Suspense, useLayoutEffect, useRef, type CSSProperties, type MouseEvent, type ReactNode } from 'react'
import type { PaletteCommand } from './CommandPalette'
import { useEffect, useState } from 'react'
import { useToast } from './providers/ToastProvider'
import { useTheme } from './providers/ThemeProvider'
import { Lattice } from './ambient/LatticeCanvas'
import { installPointerLight } from './ambient/pointerLight'
import { LogoMark } from '../ui/Logo'
import { Sheet, SheetContent } from '../ui/Sheet'
import { useReducedMotion } from '../ui/motion'
import {
  Activity,
  BookOpen,
  Boxes,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  LayoutDashboard,
  Menu,
  Network,
  ScrollText,
  Server,
  ShieldAlert,
  Stethoscope,
  SunMoon,
  Terminal,
  UsersRound,
  type LucideIcon,
} from 'lucide-react'

const LazyCommandPalette = lazy(() => import('./CommandPalette').then((module) => ({ default: module.CommandPalette })))

const links: readonly [string, string, LucideIcon][] = [
  ['/app/', 'Overview', LayoutDashboard],
  ['/app/rent', 'Rent', Server],
  ['/app/rent/hosts', 'Browse hosts', Server],
  ['/app/instances', 'Instances', Boxes],
  ['/app/billing', 'Billing', CircleDollarSign],
  ['/app/advanced/sdl', 'SDL editor', Terminal],
  ['/app/advanced/activity', 'Activity', Activity],
  ['/app/docs', 'Guides', BookOpen],
] as const

const operatorLinks: readonly [string, string, LucideIcon][] = [
  ['/app/ops', 'Fleet overview', Network],
  ['/app/ops/hosts', 'Host detail', Server],
  ['/app/ops/workloads', 'Workloads', Boxes],
  ['/app/ops/failover', 'Failover', ShieldAlert],
  ['/app/ops/diagnostics', 'Diagnostics', Stethoscope],
  ['/app/ops/logs', 'Logs', ScrollText],
  ['/app/ops/admin', 'Users and roles', UsersRound],
  ['/app/ops/versions', 'Versions', ScrollText],
] as const

function NavigationLinks({ path, role, onNavigate, onClose }: { path: string; role?: string | undefined; onNavigate: (path: string) => void; onClose?: () => void }) {
  const visibleOperatorLinks = operatorLinks.filter(([href]) => href !== '/app/ops/admin' || role === 'admin')
  const isActive = (href: string) => href === '/app/'
    ? path === '/app' || path === '/app/'
    : href === '/app/rent'
      ? path === href
      : path === href || path.startsWith(`${href}/`)
  const navigate = (event: MouseEvent<HTMLAnchorElement>, href: string) => { event.preventDefault(); onClose?.(); onNavigate(href) }
  return <>
    <p className="nav-label">Workspace</p>
    {links.map(([href, label, Icon]) => <a key={href} href={href} aria-current={isActive(href) ? 'page' : undefined} className={isActive(href) ? 'nav-link nav-link-active' : 'nav-link'} onClick={(event) => navigate(event, href)}><span aria-hidden="true" className="nav-glyph"><Icon size={16} strokeWidth={1.8} /></span>{label}</a>)}
    {['operator', 'admin'].includes(role ?? '') && <><p className="nav-label">Operations</p>{visibleOperatorLinks.map(([href, label, Icon]) => <a key={href} href={href} aria-current={isActive(href) ? 'page' : undefined} className={isActive(href) ? 'nav-link nav-link-active' : 'nav-link'} onClick={(event) => navigate(event, href)}><span aria-hidden="true" className="nav-glyph"><Icon size={16} strokeWidth={1.8} /></span>{label}</a>)}</>}
  </>
}

/**
 * The sidebar's one light: a lit strip that travels to the page you chose.
 *
 * Measured from the link that carries `aria-current`, so the indicator can
 * never disagree with what assistive technology is told is current. It slides
 * between links (ambience.css) because that is the change a click made; with
 * no current link — a route outside the navigation — it goes dark.
 */
function useNavIndicator(path: string, deps: readonly unknown[]) {
  const navRef = useRef<HTMLElement>(null)
  const [style, setStyle] = useState<CSSProperties>({ opacity: 0 })
  // The first placement is not a move: the strip appears in place, and only
  // later changes of page slide it.
  const [settled, setSettled] = useState(false)
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(true), 60)
    return () => window.clearTimeout(timer)
  }, [])
  useLayoutEffect(() => {
    const measure = () => {
      const active = navRef.current?.querySelector<HTMLElement>('[aria-current="page"]')
      setStyle(active
        ? { '--nav-y': `${active.offsetTop}px`, '--nav-h': `${active.offsetHeight}px`, opacity: 1 } as CSSProperties
        : { opacity: 0 })
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
    // The links move when the role adds a section or the rail collapses.
  }, [path, ...deps])
  return { navRef, style, settled }
}

/**
 * The page arrives as one movement when the route changes: a short rise out
 * of a blur. Played through the Web Animations API on the existing <main>, so
 * nothing remounts and no route loses its state to an animation; skipped with
 * reduced motion, and where the API does not exist.
 */
function useRouteReveal(path: string) {
  const stageRef = useRef<HTMLElement>(null)
  const reduced = useReducedMotion()
  useEffect(() => {
    const stage = stageRef.current
    if (!stage || typeof stage.animate !== 'function' || reduced) return undefined
    const animation = stage.animate(
      [
        { opacity: 0, transform: 'translateY(10px)', filter: 'blur(5px)' },
        { opacity: 1, transform: 'none', filter: 'blur(0)' },
      ],
      { duration: 520, easing: 'cubic-bezier(.16, 1, .3, 1)' },
    )
    return () => animation.cancel()
  }, [path, reduced])
  return stageRef
}

export function AppShell({ path, children, topbar, footer, onNavigate, role, commands = [] }: { path: string; children: ReactNode; topbar: ReactNode; footer: ReactNode; onNavigate: (path: string) => void; role?: string | undefined; commands?: readonly PaletteCommand[] }) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const mobileTriggerRef = useRef<HTMLButtonElement>(null)
  const wasMobileOpen = useRef(false)
  const [collapsed, setCollapsed] = useState(() => window.localStorage.getItem('d.sidebar') === 'collapsed')
  const { toast } = useToast()
  const { theme, cycle: cycleTheme } = useTheme()
  const { navRef, style: indicatorStyle, settled: indicatorSettled } = useNavIndicator(path, [role, collapsed])
  const stageRef = useRouteReveal(path)
  useEffect(() => { if (wasMobileOpen.current && !mobileOpen) mobileTriggerRef.current?.focus(); wasMobileOpen.current = mobileOpen }, [mobileOpen])
  useEffect(() => { const close = (event: KeyboardEvent) => { if (event.key === 'Escape') setMobileOpen(false) }; const on = (event: Event) => toast((event as CustomEvent).detail?.message); window.addEventListener('keydown', close); window.addEventListener('ddws:toast', on); return () => { window.removeEventListener('keydown', close); window.removeEventListener('ddws:toast', on) } }, [toast])
  useEffect(() => installPointerLight(), [])
  const toggleTheme = (event: MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    cycleTheme({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 })
  }
  return <div className={collapsed ? 'app-shell rail' : 'app-shell'}>
    <div className="ambient" aria-hidden="true"><Lattice mode="ambient" /></div>
    <a className="skip-link" href="#main">Skip to content</a>
    <aside className="sidebar"><div className="sidebar-brand"><LogoMark className="sidebar-mark" /><span>DDWS <small>Console</small></span></div><nav ref={navRef} className="nav-rail" aria-label="Primary navigation"><NavigationLinks path={path} role={role} onNavigate={onNavigate} /><span className="nav-indicator" aria-hidden="true" data-settled={indicatorSettled ? '' : undefined} style={indicatorStyle} /></nav><div className="sidebar-footer"><span className="sidebar-hint">{['operator', 'admin'].includes(role ?? '') ? 'Operator workspace' : 'Cloud workspace'}</span><button className="theme-button" type="button" aria-label={`Theme: ${theme}`} onClick={toggleTheme}><SunMoon className="theme-button-icon" size={16} aria-hidden="true" /><span>Theme: {theme}</span></button><button className="sidebar-collapse" type="button" aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} onClick={() => setCollapsed((current) => { window.localStorage.setItem('d.sidebar', current ? 'expanded' : 'collapsed'); return !current })}>{collapsed ? <ChevronRight size={16} aria-hidden="true" /> : <ChevronLeft size={16} aria-hidden="true" />}<span className="sidebar-collapse-label">{collapsed ? 'Expand sidebar' : 'Collapse sidebar'}</span></button></div></aside>
    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}><SheetContent title="Console navigation" description="Choose a workspace destination." className="mobile-navigation-sheet"><nav aria-label="Mobile navigation"><NavigationLinks path={path} role={role} onNavigate={onNavigate} onClose={() => setMobileOpen(false)} /></nav></SheetContent></Sheet>
    <div className="shell-main"><div className="shell-topbar"><button ref={mobileTriggerRef} className="mobile-nav-toggle" type="button" aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'} aria-expanded={mobileOpen} onClick={() => setMobileOpen((open) => !open)}><Menu size={18} aria-hidden="true" /></button><div className="shell-topbar-content">{topbar}<Suspense fallback={null}><LazyCommandPalette extraCommands={commands} onNavigate={(next) => { setMobileOpen(false); onNavigate(next) }} onToggleTheme={() => cycleTheme({ x: window.innerWidth / 2, y: window.innerHeight / 3 })} /></Suspense></div></div><main id="main" ref={stageRef}>{children}</main>{footer}</div>
  </div>
}
